import React, { useState, useEffect, useCallback } from 'react';
import { UserCog, Save, ListOrdered } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import api from '../../lib/api';

const LEVELS = ['Member', 'Junior Member', 'Friend', 'Leadership Circle', 'Patron'];

const Toggle = ({ label, value, onChange, testId }) => (
  <label className="flex items-center gap-2 text-sm text-[#3c4a2a] cursor-pointer" data-testid={testId}>
    <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="accent-[#5d6b3c] w-4 h-4" />
    {label}
  </label>
);

export const MemberManager = ({ onSaved }) => {
  const [members, setMembers] = useState([]);
  const [email, setEmail] = useState('');
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get('/admin/members').then((res) => setMembers(res.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const pick = (em) => {
    setEmail(em);
    const m = members.find((x) => x.email === em);
    setForm(m ? {
      membership_level: m.membership_level || 'Member',
      role: m.role || 'member',
      founding_member: !!m.founding_member,
      founding_number: m.founding_number || '',
      leadership_access: !!m.leadership_access,
      member_id: m.member_id || '',
      title: m.title || '',
      new_password: '',
    } : null);
  };

  const [renumbering, setRenumbering] = useState(false);

  const renumberAll = async () => {
    if (!window.confirm('Renumber ALL members? Founding members get the first numbers (by founding #), then everyone else in join order. This overwrites existing member numbers.')) return;
    setRenumbering(true);
    try {
      const res = await api.post('/admin/members/renumber');
      toast.success(res.data.message);
      load();
      if (email) pick('');
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Renumbering failed.');
    } finally {
      setRenumbering(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put('/admin/members/update', {
        email,
        membership_level: form.membership_level,
        role: form.role,
        founding_member: form.founding_member,
        founding_number: form.founding_member && form.founding_number !== '' ? Number(form.founding_number) : 0,
        leadership_access: form.leadership_access,
        member_id: form.member_id,
        title: form.title,
        new_password: form.new_password || null,
      });
      if (form.new_password) setForm({ ...form, new_password: '' });
      toast.success(res.data.message);
      load();
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#f1e3eb]/60 rounded-xl p-5 mb-6" data-testid="member-manager">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-serif text-lg text-[#3c4a2a] flex items-center gap-2 mb-1">
            <UserCog className="w-5 h-5 text-[#a3688f]" /> Manage a Member
          </h3>
          <p className="text-sm text-[#3c4a2a]/60">Set membership level, member number, founding number, admin access, and Leadership Library access.</p>
        </div>
        <Button
          variant="outline"
          onClick={renumberAll}
          disabled={renumbering}
          data-testid="renumber-all-btn"
          className="border-[#a3688f] text-[#a3688f] hover:bg-[#a3688f] hover:text-white flex-shrink-0"
        >
          <ListOrdered className="w-4 h-4 mr-2" />
          {renumbering ? 'Renumbering…' : 'Renumber All Members'}
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 items-end">
        <div>
          <Label className="text-[#3c4a2a]">Member</Label>
          <select
            value={email}
            onChange={(e) => pick(e.target.value)}
            data-testid="manager-member-select"
            className="mt-1 w-full h-10 rounded-md border border-[#bd8cae]/50 bg-white px-3 text-sm text-[#3c4a2a]"
          >
            <option value="">Select a member…</option>
            {members.map((m) => (
              <option key={m.id} value={m.email}>{m.first_name} {m.last_name} — {m.email}</option>
            ))}
          </select>
        </div>
        {form && (
          <div>
            <Label className="text-[#3c4a2a]">Membership Level</Label>
            <select
              value={form.membership_level}
              onChange={(e) => setForm({ ...form, membership_level: e.target.value })}
              data-testid="manager-level-select"
              className="mt-1 w-full h-10 rounded-md border border-[#bd8cae]/50 bg-white px-3 text-sm text-[#3c4a2a]"
            >
              {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        )}
      </div>
      {form && (
        <div className="mt-4 grid sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-[#3c4a2a]">Member Number</Label>
            <Input
              value={form.member_id}
              onChange={(e) => setForm({ ...form, member_id: e.target.value })}
              placeholder="e.g., WCLC-2026-0001"
              data-testid="manager-member-id-input"
              className="mt-1 bg-white"
            />
          </div>
          <div>
            <Label className="text-[#3c4a2a]">Title / Office</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g., President, Treasurer"
              data-testid="manager-title-input"
              className="mt-1 bg-white"
            />
          </div>
          <div>
            <Label className="text-[#3c4a2a]">Reset Password</Label>
            <Input
              type="text"
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              placeholder="New password (min 8 chars)"
              data-testid="manager-password-input"
              className="mt-1 bg-white"
            />
          </div>
        </div>
      )}
      {form && (
        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-3">
          <Toggle label="Founding Member" value={form.founding_member} onChange={(v) => setForm({ ...form, founding_member: v })} testId="manager-founding-toggle" />
          {form.founding_member && (
            <div className="flex items-center gap-2">
              <Label className="text-[#3c4a2a] text-sm">Founding #</Label>
              <Input
                type="number"
                min="1"
                value={form.founding_number}
                onChange={(e) => setForm({ ...form, founding_number: e.target.value })}
                data-testid="manager-founding-number"
                className="w-24 bg-white"
              />
            </div>
          )}
          <Toggle label="Leadership Library access" value={form.leadership_access} onChange={(v) => setForm({ ...form, leadership_access: v })} testId="manager-leadership-toggle" />
          <Toggle label="Full admin access" value={form.role === 'admin'} onChange={(v) => setForm({ ...form, role: v ? 'admin' : 'member' })} testId="manager-admin-toggle" />
          <Button onClick={save} disabled={saving} data-testid="manager-save-btn" className="bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white ml-auto">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      )}
    </div>
  );
};
